import { SupportedChainId } from '~/constants/chains'
import { PendingPricingCall } from '~/hooks/useTokenPrices/index'
import { retryExponentialBackoff } from '~/utils/requests'
import { getServerOrigin } from '~/utils/serverOrigin'

type TokenPrice = {
  address: string
  chainId: SupportedChainId
  price: number
}

type TokenPricesResponse = {
  prices: Record<string, Record<string, number>>
}

type FetchPricesOptions = {
  transactionTime?: string | number
  transactionHash?: string
}

export const fetchPrices = async (
  pendingCalls: PendingPricingCall[],
  options: FetchPricesOptions = {},
): Promise<TokenPrice[]> => {
  if (pendingCalls.length === 0) return []

  const pricingURL = buildFetchPricesURL(
    pendingCalls,
    options.transactionTime,
    options.transactionHash,
  )

  const tokenPriceResponse = await retryExponentialBackoff(
    fetch,
    [pricingURL],
    {
      maxTry: 3,
    },
  )

  if (!tokenPriceResponse.ok) {
    // eslint-disable-next-line no-console
    console.error(
      `Invalid pricing response: ${tokenPriceResponse.status} - ${tokenPriceResponse.statusText}`,
    )
    return []
  }

  const tokenPricesJSON: TokenPricesResponse = await tokenPriceResponse.json()

  const tokenPrices: TokenPrice[] = []

  Object.entries(tokenPricesJSON.prices).forEach(
    ([chainIdAsString, tokensObject]) => {
      const chainId = parseInt(chainIdAsString) as SupportedChainId
      Object.entries(tokensObject).forEach(([address, price]) => {
        tokenPrices.push({
          address,
          chainId,
          price,
        })
      })
    },
  )

  return tokenPrices
}

const buildFetchPricesURL = (
  pendingCalls: PendingPricingCall[],
  transactionTime?: string | number,
  transactionHash?: string,
): string => {
  const callsByChain = pendingCalls.reduce(
    (acc, call) => {
      if (!acc[call.chainId]) {
        acc[call.chainId] = []
      }
      acc[call.chainId].push(call.address)
      return acc
    },
    {} as Record<SupportedChainId, string[]>,
  )

  // Build query string
  const query = Object.entries(callsByChain)
    .map(([chainId, addresses]) => {
      if (addresses.length === 0) return undefined
      const tokenString = addresses.join(',')
      return [chainId, tokenString].join(':')
    })
    .filter(Boolean)
    .join('::')

  const url = new URL(getServerOrigin())
  url.pathname = '/api/public/pricing'
  url.searchParams.append('query', query)

  if (transactionTime) {
    if (!transactionHash) {
      throw new Error(
        'Transaction hash is required when transaction time is provided',
      )
    }
    url.searchParams.append('transactionTime', transactionTime.toString())
    url.searchParams.append('transactionHash', transactionHash)
  }

  return url.toString()
}
